import { DataLoader } from './data/DataLoader';
import { GameStateManager } from './game/GameState';
import { Game } from './game/Game';
import { GameController } from './game/GameController';
import { UIManager } from './ui/UIManager';
import { AudioManager } from './audio/AudioManager';
import type { OrderType } from './game/types';
import './styles/main.css';

async function init() {
  // Load all game data
  const dataLoader = DataLoader.getInstance();
  const gameData = await dataLoader.loadAll();

  // Get app container
  const app = document.querySelector('#app');
  if (!app) {
    throw new Error('App container not found');
  }

  // Create initial empty game state (no scientists yet)
  const gameState = new GameStateManager(gameData);

  // Declare game and controller variables that will be initialized after scientist selection
  let game: Game;
  let gameController: GameController;
  
  // Create order handler that will be connected to gameController after initialization
  let orderHandler: (order: OrderType | 'deselect' | 'cancel_synthetize' | 'cancel_rest', ...args: unknown[]) => void;

  // Initialize UI (needed to show scientist selection modal)
  const uiManager = new UIManager(
    app as HTMLElement,
    gameState,
    gameData,
    (order, ...args) => {
      // GameController will be initialized after game is created
      if (orderHandler) {
        orderHandler(order, ...args);
      }
    },
    (speed) => {
      gameState.setGameSpeed(speed);
      uiManager.update();
    }
  );

  // Show scientist selection modal and wait for selection
  const selectedScientists = await uiManager.showScientistSelection();

  // Initialize game state with selected scientists
  gameState.initializeWithScientists(selectedScientists);

  // Initialize game with the populated game state
  game = new Game(gameData, gameState);

  // Create game controller to handle orders and game logic
  gameController = new GameController(game, gameState, gameData, uiManager);
  
  // Connect order handler
  orderHandler = (order, ...args) => gameController.handleOrder(order, ...args);

  // Set reality breach system after game initialization
  uiManager.setRealityBreachSystem(game.getRealityBreachSystem());

  // Setup cell click handling for move orders
  gameController.setupCellClickHandling(app as HTMLElement);

  // Update UI using requestAnimationFrame for smooth animations
  let animationFrameId: number | null = null;
  const updateUI = () => {
    uiManager.update();
    animationFrameId = requestAnimationFrame(updateUI);
  };
  animationFrameId = requestAnimationFrame(updateUI);

  // Start game
  game.start();
  uiManager.update();

  // Start background music after user interaction (scientist selection)
  AudioManager.getInstance().startBackgroundMusic();
}

init().catch((error) => {
  console.error('Failed to initialize game:', error);
  const app = document.querySelector('#app');
  if (app) {
    app.innerHTML = `
      <div style="padding: 20px; color: red;">
        <h1>Failed to Load Game</h1>
        <p>${error instanceof Error ? error.message : 'Unknown error occurred'}</p>
        <p>Please refresh the page to try again.</p>
      </div>
    `;
  }
});

